import axios from 'axios';
import TelemetryService from 'src/services/telemetry';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

export function useTraceCycle({ props, emit } = {}) {
  const route = useRoute();
  const router = useRouter();
  const store = useStore();

  // Refs to external DOM (wire up from the host)
  const videoEl = ref(null);
  const boundingBoxesEl = ref(null);

  // State (parity with RecordedInference.vue)
  const isGoodCycle = ref(false);
  const frame = ref(0);
  const currentStepList = ref([]);
  const currentCycle = ref(1);
  const filePreds = ref(null);
  const stepsStartPoints = ref({});
  const stepsTime = ref({});
  const substepList = ref([]);
  const backgroundTime = ref(0);
  const missedSteps = ref([]);
  const detectedSteps = ref({});
  const isPredictionFileNotExist = ref(false);
  const processDetails = ref({});
  const showVideoControls = ref(false);
  const propagatedSteps = ref([]);
  const traceCycleData = ref(null);
  const currentVideoTime = ref(0);
  const missedStepsFromTelemetry = ref([]);
  const cycleTime = ref(0);
  const taskId = ref(null);
  const historyId = ref(null);
  const animationFrameId = ref(0);

  // Client like UI

  // Getters from store
  const negativeSteps = computed(() => store.getters['negativeSteps']);
  const optionalSteps = computed(() => store.getters['optionalSteps']);
  const taskStepsCount = computed(() => store.getters['taskStepsCount']);
  const isFetchingTaskDetails = computed(
    () => store.getters['isFetchingTaskDetails']
  );
  const stepsTranslationMapping = computed(
    () => store.getters['stepsTranslationMapping']
  );
  const role = computed(() => store.getters['role']);

  // Computed (parity)
  const cycleType = computed(() =>
    !traceCycleData.value?.is_good_cycle ? 'Good' : 'Bad'
  );
  const isMetaExist = computed(
    () =>
      traceCycleData.value?.metadata &&
      Object.keys(traceCycleData.value?.metadata).length
  );

  const currentStepsText = computed(() => {
    try {
      const currentSteps = filePreds.value[frame.value]['pred'];
      if (currentSteps[0] === taskStepsCount.value) return ['● Background'];
      return currentSteps.map(
        (substep) => `● ${substepList.value[substep]?.name}`
      );
    } catch (error) {
      console.log(error);
      return [];
    }
  });

  const nextStepText = computed(() => {
    try {
      const nextStep = filePreds.value[frame.value]['next_step'];
      return `● ${substepList.value[nextStep]?.name}`;
    } catch (error) {
      console.log('error in next steps', error);
      return null;
    }
  });
  const potentialIssuesText = computed(() => {
    try {
      const missedStep = filePreds.value[frame.value]['missed_steps'];
      return missedStep.map(
        (substep) => `● ${substepList.value[substep]?.name}`
      );
    } catch (error) {
      console.log('error in potential issue', error);
      return [];
    }
  });

  const stepsCompleted = computed(() => {
    return Object.entries(stepsTime.value).filter(
      ([step, time]) =>
        time > 0 && !missedStepsFromTelemetry.value.includes(step)
    ).length;
  });

  // Helpers
  function setStepsStartTime(list = substepList.value) {
    stepsTime.value = list.reduce((acc, el) => {
      acc[el.substepIndex] = 0;
      return acc;
    }, {});
  }

  function setStepStartPoint(preds) {
    const start = {};
    Object.entries(preds).forEach(([frameKey, value]) => {
      value.pred.forEach((k) => {
        if (!(k in start)) start[k] = frameKey;
      });
    });
    stepsStartPoints.value = start;
  }

  function addStepInDetectedStepsDict(step) {
    const tmp = { ...detectedSteps.value };
    if (missedStepsFromTelemetry.value?.includes(step)) tmp[step] = false;
    else tmp[step] = true;
    detectedSteps.value = tmp;
  }

  function updateSubstepTimes(f = frame.value) {
    const stepsObj = filePreds.value?.[f];
    if (!stepsObj) return;

    stepsObj.pred = stepsObj?.pred.filter(
      (el) => !missedStepsFromTelemetry.value?.includes(el)
    );

    const tmp = { ...stepsTime.value };
    const { pred, times } = stepsObj;

    // Background class
    if (pred[0] === taskStepsCount.value) {
      backgroundTime.value = times[0] || 0;
      return;
    }

    cycleTime.value += times[0] - (stepsTime.value?.[pred[0]] || 0);
    pred.forEach((step, idx) => {
      tmp[step] = times[idx];
    });
    stepsTime.value = tmp;
  }

  // Video sync
  function highlightCurrentStep() {
    try {
      const current = videoEl.value?.currentTime?.toFixed(5);
      currentVideoTime.value = current;
      const num = (Number(current) || 0) * (props?.video?.fps || 30);
      frame.value = Math.floor(num);
    } catch (e) {
      resetState();
    }
  }

  function onLoad() {
    showVideoControls.value = true;
    boundingBoxesEl.value?.setupCanvas?.();
  }

  function onPlay() {
    const loop = () => {
      highlightCurrentStep();
      boundingBoxesEl.value?.drawBoundingBoxes?.();
      animationFrameId.value = requestAnimationFrame(loop);
    };
    animationFrameId.value = requestAnimationFrame(loop);
  }

  function onPause() {
    cancelAnimationFrame(animationFrameId.value);
  }

  function onEnd() {
    cycleTime.value = 0;
    backgroundTime.value = 0;
    detectedSteps.value = {};
    currentCycle.value = 1;
    currentStepList.value = [];
    cancelAnimationFrame(animationFrameId.value);
  }

  function handleSeeking(e) {
    const current = videoEl.value?.currentTime?.toFixed(5);
    currentVideoTime.value = current;
    if (isPredictionFileNotExist.value) return;

    setStepsStartTime();
    cycleTime.value = 0;

    const fps = props?.video?.fps || 30;
    const currentFrame = Math.floor((e?.target?.currentTime || 0) * fps);
    frame.value = currentFrame;
    for (let i = 0; i <= currentFrame; i++) {
      updateSubstepTimes(i);
    }
    boundingBoxesEl.value?.drawBoundingBoxes?.(currentFrame);
  }

  function skipTo(timeInSec) {
    if (videoEl.value) videoEl.value.currentTime = timeInSec;
  }

  // Data fetching
  async function handleGetTraceRecordData(query) {
    if (props?.video?.cycleData) {
      traceCycleData.value = props.video.cycleData;
      isGoodCycle.value = props.video.cycleData?.is_good_cycle;
      return;
    }
    const [error, data] = await TelemetryService.getTraceCycleRecordData(query);
    if (error) return;

    traceCycleData.value = data.cycle;
    isGoodCycle.value = data?.cycle?.is_good_cycle;
    if (data?.cycle?.history_id) historyId.value = data.cycle.history_id;
    if (data?.task_record?.Task) taskId.value = data?.task_record?.Task;
  }

  async function handleFetchRequiredData() {
    const { traceId } = route.params;
    const { cycle_id } = route.query;
    let query;
    if (traceId && cycle_id) {
      query = { cycle_id, task_record_id: traceId };
    } else {
      query = {
        cycle_id: props?.video?.cycle_id,
        task_record_id: props?.video?.id,
      };
    }

    if (query.cycle_id && query.task_record_id) {
      await handleGetTraceRecordData(query);
      await store.dispatch('fetchTaskDetails', {
        taskId: Number(taskId.value),
        historyId: historyId.value,
      });
    }

    await updatePredictions();
    updateMissedSteps();
  }

  function updateMissedSteps() {
    if (!props?.video || isGoodCycle.value) return;
    missedSteps.value = props.video.missedStepsName;
  }

  async function updatePredictions() {
    if (!props?.video) return;
    const { per_frame_prediction_file_url } = props.video;
    try {
      const { data } = await axios.get(per_frame_prediction_file_url);
      const { preds, propagated_steps } = data;
      emit?.('showVisualization', !!preds[0]['bboxes']);
      setStepStartPoint(preds);
      propagatedSteps.value = propagated_steps || [];
      filePreds.value = preds;
    } catch (e) {
      isPredictionFileNotExist.value = true;
      filePreds.value = null;
      detectedSteps.value = {};
    }
  }

  async function toggleCycleStatus() {
    const val = !traceCycleData.value?.is_good_cycle;
    if (traceCycleData.value) traceCycleData.value.is_good_cycle = val;
    isGoodCycle.value = val;
    updateMissedSteps();
    emit?.('toggleCycleStatus', { video: props.video, isGoodCycle: val });
  }

  async function archiveCycle(video) {
    await TelemetryService.archiveCycle(video.cycle_id);
    emit?.('archivedCycleCount', video.is_good_cycle);
    if (props?.navigate) router.push('/');
  }

  function handleClickSubStep(stepIndex) {
    if (!(stepIndex in stepsStartPoints.value)) return;
    const f = stepsStartPoints.value[stepIndex];
    const fps = props?.video?.fps || 30;
    skipTo(f / fps);
  }

  // Wire refs from host
  function attachVideo(el) {
    console.log('attach video', el);
    videoEl.value = el;
  }

  function attachBoundingBoxes(el) {
    boundingBoxesEl.value = el;
  }

  function resetState() {
    frame.value = 0;
    currentStepList.value = [];
    currentCycle.value = 1;
    detectedSteps.value = {};
    isPredictionFileNotExist.value = false;
    stepsTime.value = {};
    cancelAnimationFrame(animationFrameId.value);
  }

  // Watches
  watch(
    () => props?.video,
    (nv, ov) => {
      if (nv?.cycle_id !== ov?.cycle_id) {
        handleFetchRequiredData();

        const steps = nv?.missed_steps?.split(', ');
        if (steps?.length) {
          missedStepsFromTelemetry.value = steps.map((s) =>
            s ? Number(s) : ''
          );
        }
      }
      updateMissedSteps();
    },
    { deep: true }
  );

  watch(frame, (currentFrame) => {
    currentStepList.value =
      filePreds.value?.[currentFrame]?.pred?.filter(
        (el) => !missedStepsFromTelemetry.value?.includes(el)
      ) || [];
    updateSubstepTimes();
  });

  watch(
    () => props?.stepsList,
    (list) => {
      const onlySub = (list || []).filter((el) => el.type === 'sub-step');
      substepList.value = onlySub;
    },
    { deep: true, immediate: true }
  );

  watch(currentStepList, (list) => list.forEach(addStepInDetectedStepsDict));
  watch(currentCycle, () => {
    detectedSteps.value = {};
    stepsTime.value = {};
  });

  // Mount
  onMounted(() => {
    const temp = (props?.stepsList || []).filter(
      (el) => el.type === 'sub-step'
    );
    substepList.value = temp;
    setStepsStartTime(temp);
    handleFetchRequiredData();
  });

  onBeforeUnmount(() => {
    resetState();
  });

  return {
    // DOM refs hookups
    videoEl,
    boundingBoxesEl,
    attachVideo,
    attachBoundingBoxes,

    // Store-derived
    negativeSteps,
    optionalSteps,
    isFetchingTaskDetails,
    stepsTranslationMapping,
    role,

    // State
    isGoodCycle,
    frame,
    currentStepList,
    currentCycle,
    filePreds,
    stepsStartPoints,
    stepsTime,
    substepList,
    backgroundTime,
    missedSteps,
    detectedSteps,
    isPredictionFileNotExist,
    processDetails,
    showVideoControls,
    propagatedSteps,
    traceCycleData,
    currentVideoTime,
    cycleTime,
    missedStepsFromTelemetry,
    currentStepsText,
    nextStepText,
    potentialIssuesText,
    taskStepsCount,
    stepsCompleted,

    // Computed
    cycleType,
    isMetaExist,

    // Actions
    setStepsStartTime,
    setStepStartPoint,
    updateSubstepTimes,
    addStepInDetectedStepsDict,
    onLoad,
    onPlay,
    onPause,
    onEnd,
    handleSeeking,
    highlightCurrentStep,
    skipTo,
    handleFetchRequiredData,
    handleGetTraceRecordData,
    updateMissedSteps,
    updatePredictions,
    toggleCycleStatus,
    archiveCycle,
    handleClickSubStep,
    resetState,
  };
}
